import type { ReactNode } from 'react';
import { Info, Warning, Lightbulb, CheckCircle } from '@phosphor-icons/react';
import { cn } from '@/lib/utils/cn';

type Variant = 'info' | 'warning' | 'tip' | 'success';

const VARIANT_STYLES: Record<
  Variant,
  { border: string; bg: string; icon: string; Icon: typeof Info }
> = {
  info: {
    border: 'border-neurax-electric/30',
    bg: 'bg-neurax-electric/[0.06]',
    icon: 'text-neurax-electric',
    Icon: Info,
  },
  warning: {
    border: 'border-neurax-amber/30',
    bg: 'bg-neurax-amber/[0.06]',
    icon: 'text-neurax-amber',
    Icon: Warning,
  },
  tip: {
    border: 'border-neurax-plasma/30',
    bg: 'bg-neurax-plasma/[0.06]',
    icon: 'text-neurax-plasma',
    Icon: Lightbulb,
  },
  success: {
    border: 'border-neurax-mint/30',
    bg: 'bg-neurax-mint/[0.06]',
    icon: 'text-neurax-mint',
    Icon: CheckCircle,
  },
};

interface Props {
  variant?: Variant;
  title?: string;
  children: ReactNode;
}

export function GuideCallout({ variant = 'info', title, children }: Props) {
  const styles = VARIANT_STYLES[variant];
  const { Icon } = styles;
  return (
    <div className={cn('flex items-start gap-3 rounded-2xl border p-4', styles.border, styles.bg)}>
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', styles.icon)} />
      <div className="flex-1 text-sm text-muted-foreground [&_a]:text-neurax-electric [&_a]:underline [&_strong]:font-semibold [&_strong]:text-white">
        {title && <p className="mb-1 font-semibold text-white">{title}</p>}
        {children}
      </div>
    </div>
  );
}
